import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { WB_ModelPredictorRow } from "../PA.Core/PAdbSchemaRows";
import { IwbModelPredictor } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Model_Predictor,
    esWB_Modeling_SessionPR,
    esWB_Simulation_Predictor_Relationship,
    esWB_CBO_Set_Predictor,
    esWB_ScnPlan_PredictorR,
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { ModelPredictorBOValidations } from "./ModelPredictorBO.Validations";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { PAEntity } from "../PA.Core/PAEntity";
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";

export class ModelPredictorBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelPredictor: WB_ModelPredictorRow;

    _bIsIdEditable: boolean;

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Model Predictor" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ModelPredictor = null;
        this._bIsIdEditable = true;
    }

    get WB_ModelPredictor(): WB_ModelPredictorRow {
        return this.drWB_ModelPredictor;
    }

    get bIsIdEditable(): boolean {
        return this._bIsIdEditable;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Model Predictor");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new Model Predictor.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext
                .createRecordWithKey(esWB_Model_Predictor)
                .then((newModelPredictorRow: WB_ModelPredictorRow) => {
                    newModelPredictorRow.setDefaults();
                    self.drWB_ModelPredictor = newModelPredictorRow;
                    self.dbContext.add(esWB_Model_Predictor, newModelPredictorRow);
                    self.Identity.setAll(
                        self.WB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                        self.WB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                        self.WB_ModelPredictor.cif.MODEL_PREDICTOR_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ModelPredictor,
                        "WB_MODEL_PREDICTOR_ID",
                        "MODEL_PREDICTOR_ID",
                        "MODEL_PREDICTOR_NAME",
                    );
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._bIsIdEditable = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                let sError = "Model Predictor already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null) self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Model Predictor.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = ModelPredictorBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = ModelPredictorBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let newModelPredictorRow: any = recordResolver.get(esWB_Model_Predictor, anId);

                    if (_.isEmpty(newModelPredictorRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ModelPredictor = newModelPredictorRow;
                    self.Identity.setAll(
                        self.WB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                        self.WB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                        self.WB_ModelPredictor.cif.MODEL_PREDICTOR_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ModelPredictor,
                        "WB_MODEL_PREDICTOR_ID",
                        "MODEL_PREDICTOR_ID",
                        "MODEL_PREDICTOR_NAME",
                    );
                })
                .then((newModelPredictorRow: any) => {
                    let aPAEntity = new PAEntity();
                    return aPAEntity.checkCustomColumnExists(
                        "ModelPredictor",
                        self.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                        "/AZR/WB_SIMULATION_PREDICTORR",
                        self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                    );
                })
                .then((bExist: boolean) => {
                    self._bIsIdEditable = !bExist;
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start(
            "Validating " +
                self.WB_ModelPredictor.cif.MODEL_PREDICTOR_NAME +
                "(" +
                self.WB_ModelPredictor.cif.MODEL_PREDICTOR_ID +
                ")",
        );

        // Does no validations yet
        self.ValidateOptions = options || {};
        try {
            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate Simulation Input.";
                self.ValidateProgressProvider.exception(sError);
                self._bIsValidated = false;
                return self;
            }

            await ModelPredictorBOValidations.ValidateFields(self);

            self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
            self.ValidateProgressProvider.finish();
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self;
        }

        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        let aPAEntity = new PAEntity();
        const wbModelPredictor = <IwbModelPredictor>self.drWB_ModelPredictor;

        self.SaveProgressProvider.start(
            "Saving " + wbModelPredictor.MODEL_PREDICTOR_NAME + "(" + wbModelPredictor.MODEL_PREDICTOR_ID + ")",
        );
        self._bIsSaved = false;
        self.SaveOptions = options || {};

        try {
            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext to Save Model Predictor.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any,
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let tasks: Promise<any>[] = [];

            await self.Validate({});

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_Model_Predictor, self.drWB_ModelPredictor, {});

            await aPAEntity.createOrUpdateCustomColumn(
                esWB_Model_Predictor,
                self.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                "/AZR/WB_SIMULATION_PREDICTORR",
                self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                {
                    APP_ID: self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                    MAPPER_ID: esWB_Simulation_Predictor_Relationship,
                    SERVER_MODEL_ID: esWB_Simulation_Predictor_Relationship,
                    COLUMN_DATATYPE: "DECIMAL",
                    COLUMN_LENGTH: 15,
                    COLUMN_PRECISION: 6,
                    NOTES: self.drWB_ModelPredictor.cif.COMMENTS || "",
                },
            );

            await aPAEntity.createOrUpdateCustomColumn(
                esWB_CBO_Set_Predictor,
                self.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                "/AZR/WB_CBO_SET_PR",
                self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                {
                    APP_ID: self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                    MAPPER_ID: esWB_CBO_Set_Predictor,
                    SERVER_MODEL_ID: esWB_CBO_Set_Predictor,
                    COLUMN_DATATYPE: "DECIMAL",
                    COLUMN_LENGTH: 15,
                    COLUMN_PRECISION: 6,
                    NOTES: self.drWB_ModelPredictor.cif.COMMENTS || "",
                },
            );
            await aPAEntity.createOrUpdateCustomColumn(
                esWB_Modeling_SessionPR,
                self.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                "/AZR/WB_MODELING_SESSIONPR",
                self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                {
                    APP_ID: self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                    MAPPER_ID: esWB_Modeling_SessionPR,
                    SERVER_MODEL_ID: esWB_Modeling_SessionPR,
                    COLUMN_DATATYPE: "DECIMAL",
                    COLUMN_LENGTH: 15,
                    COLUMN_PRECISION: 6,
                    NOTES: self.drWB_ModelPredictor.cif.COMMENTS || "",
                },
            );

            await aPAEntity.createOrUpdateCustomColumn(
                esWB_ScnPlan_PredictorR,
                self.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                "/AZR/WB_SCNPLAN_PREDICTORR",
                self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                {
                    APP_ID: self.drWB_ModelPredictor.cif.MODEL_PREDICTOR_ID,
                    MAPPER_ID: esWB_ScnPlan_PredictorR,
                    SERVER_MODEL_ID: esWB_ScnPlan_PredictorR,
                    COLUMN_DATATYPE: "DECIMAL",
                    COLUMN_LENGTH: 15,
                    COLUMN_PRECISION: 6,
                    NOTES: self.drWB_ModelPredictor.cif.COMMENTS || "",
                },
            );

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                    LeaseRegistry.generateLeaseInvalidationByClassList([
                        LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)),
                        LeaseRegistry.getLeaseClass(
                            LeaseRegistry.getLeaseClass(
                                LeaseRegistry.Lease_Subject_Model_Predictor,
                                this.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                            ),
                        ),
                    ]);
                });
            } else {
                self._bIsSaved = true;
                LeaseRegistry.generateLeaseInvalidationByClassList([
                    LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)),
                    LeaseRegistry.getLeaseClass(
                        LeaseRegistry.getLeaseClass(
                            LeaseRegistry.Lease_Subject_Model_Predictor,
                            this.drWB_ModelPredictor.cif.WB_MODEL_PREDICTOR_ID,
                        ),
                    ),
                ]);
            }

            self._bIsIdEditable = false;
            self.SaveProgressProvider.finish();
        } catch (err) {
            self._bIsSaved = false;
            self.SaveProgressProvider.exception(err);
        }

        return self;
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModelPredictor = <IwbModelPredictor>self.drWB_ModelPredictor;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbModelPredictor.MODEL_PREDICTOR_NAME + "(" + wbModelPredictor.MODEL_PREDICTOR_ID + ")",
        );

        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the Model Predictor

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ModelPredictor.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const modelPredictor_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
        };

        const aRecResolver = new ESRecordResolver(dbContext, modelPredictor_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Model_Predictor, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(ModelPredictorBO, "ModelPredictorBO", "ModelPredictor");
